import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

const faqs = [
  {
    question: 'What is a tax lien sale in Arizona?',
    answer:
      'In Arizona, when property owners fail to pay their property taxes, the county treasurer sells a tax lien certificate to investors. The investor pays the delinquent taxes and earns interest (up to 16% per year) when the owner redeems the property. If the owner doesn\'t pay within 3 years, the investor can foreclose.',
  },
  {
    question: 'When do Arizona tax lien sales happen?',
    answer:
      'Arizona tax lien sales typically occur in February each year. Each county sets its own exact date. Maricopa and Pima counties usually hold their sales in early February, while most other counties hold theirs mid-to-late February.',
  },
  {
    question: 'What is the interest rate on Arizona tax liens?',
    answer:
      'Arizona tax liens earn up to 16% interest per year. The actual rate is determined at auction — investors bid the interest rate DOWN from 16%. The lowest bidder wins the lien. This makes Arizona one of the most attractive states for tax lien investing.',
  },
  {
    question: 'What are surplus funds?',
    answer:
      'When a tax-foreclosed property sells at auction for more than the taxes owed, the excess money (surplus funds) belongs to the former property owner. Many owners don\'t know these funds exist. Counties hold these funds, and former owners can claim them — sometimes for years after the sale.',
  },
  {
    question: 'How do I participate in a tax sale?',
    answer:
      'Each Arizona county runs its own tax sale, usually online. You\'ll need to register with the county treasurer\'s office, deposit funds, and bid during the auction period. Our map and search tools help you research properties before the sale.',
  },
  {
    question: 'Is my data current?',
    answer:
      'We update our database regularly from county treasurer records. Property listings, tax amounts, and auction dates are refreshed as counties publish new data. Gold members receive email alerts when new properties are added.',
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span className="font-medium text-gray-800 text-sm">{question}</span>
        <span className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-emerald-600 text-lg`}></i>
        </span>
      </button>
      {open && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function AZTaxSalesPage() {
  useSEO({
    title: 'Arizona Tax Sale Properties & Foreclosures | AZ Tax Sales | All 15 Counties',
    description: 'Browse Arizona tax foreclosure properties across all 15 counties. Interactive map, detailed property data, auction dates, and surplus funds information. Silver & Gold membership plans for serious investors. Find your next AZ tax lien investment.',
    keywords: 'Arizona tax sale, AZ tax foreclosure, tax lien investing Arizona, tax deed sale, property auction Arizona, surplus funds Arizona, Maricopa County tax sale, Pima County tax sale, real estate investment Arizona',
    canonical: '/az-tax-sales',
    ogTitle: 'Arizona Tax Sale Properties | AZ Tax Foreclosures - All 15 Counties',
    ogDescription: 'Discover Arizona tax foreclosure investment opportunities. Interactive map, property details, and auction data across all 15 AZ counties.',
    ogImage: 'https://public.readdy.ai/ai/img_res/f01abbacd3ae7d97bd16ad5e6a7a8f5c.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Arizona Tax Sale Property Search',
      description: 'Comprehensive database and tools for researching Arizona tax foreclosure properties, tax lien sales, and surplus funds across all 15 Arizona counties.',
      url: 'SITE_URL/az-tax-sales',
      provider: {
        '@type': 'Organization',
        name: 'Real Estate Development Authority',
        url: 'SITE_URL',
      },
      areaServed: {
        '@type': 'State',
        name: 'Arizona',
      },
      serviceType: 'Tax Foreclosure Property Research',
      offers: [
        {
          '@type': 'Offer',
          name: 'Free Plan',
          price: '0',
          priceCurrency: 'USD',
          description: 'Interactive county map, auction dates, and county-level data.',
        },
        {
          '@type': 'Offer',
          name: 'Silver Plan',
          price: '29',
          priceCurrency: 'USD',
          description: 'Full property details, advanced search, CSV export, and property history.',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '29',
            priceCurrency: 'USD',
            unitCode: 'MON',
          },
        },
        {
          '@type': 'Offer',
          name: 'Gold Plan',
          price: '99',
          priceCurrency: 'USD',
          description: 'Owner contact info, phone numbers, email alerts, surplus funds data, and letter templates.',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '99',
            priceCurrency: 'USD',
            unitCode: 'MON',
          },
        },
      ],
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'SITE_URL/' },
          { '@type': 'ListItem', position: 2, name: 'AZ Tax Sales', item: 'SITE_URL/az-tax-sales' },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-gray-950 text-white py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-5 leading-tight">
            Arizona Tax Foreclosure Properties
          </h1>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Discover investment opportunities across all 15 Arizona counties. Interactive maps,
            detailed property data, and owner contact information.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="https://taxsale.realestatedevelopmentauthority.net/map"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-7 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore AZ Map
            </a>
            <a
              href="https://businessdevelopmentauthority.net/ca-tax-foreclosures"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-7 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              View CA Tax Foreclosures Map
            </a>
            <a
              href="https://taxsale.realestatedevelopmentauthority.net/search"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-7 py-3 border border-white text-white hover:bg-white/10 font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Search Properties
            </a>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
            <div className="text-center">
              <div className="text-4xl font-extrabold text-emerald-400 mb-1">55</div>
              <div className="text-gray-400 text-sm">Properties Listed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-emerald-400 mb-1">15</div>
              <div className="text-gray-400 text-sm">Arizona Counties</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-emerald-400 mb-1">2025</div>
              <div className="text-gray-400 text-sm">Tax Sale Season</div>
            </div>
          </div>
        </div>
      </section>

      {/* CA Tax Foreclosures Banner */}
      <section className="bg-emerald-50 border-y border-emerald-200 py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-full flex-shrink-0">
              <i className="ri-map-pin-2-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-gray-900 font-bold text-lg leading-tight">Also investing in California?</h3>
              <p className="text-gray-600 text-sm mt-0.5">
                Explore CA Tax Foreclosure properties on our interactive California map — updated regularly with new listings.
              </p>
            </div>
          </div>
          <a
            href="https://businessdevelopmentauthority.net/ca-tax-foreclosures"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-7 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-map-2-line text-base"></i></span>
            View CA Tax Foreclosures Map
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">How It Works</h2>
          <p className="text-center text-gray-500 mb-14">Three simple steps to finding your next investment property</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                step: '1',
                title: 'Browse the Map',
                desc: 'Explore tax foreclosure properties across all 15 Arizona counties with our interactive map.',
              },
              {
                step: '2',
                title: 'Research Properties',
                desc: 'Get detailed property info, tax amounts owed, assessed values, and auction dates.',
              },
              {
                step: '3',
                title: 'Take Action',
                desc: 'Contact property owners, attend tax sales, and build your real estate portfolio.',
              },
            ].map((item) => (
              <div key={item.step} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-500 text-white text-xl font-bold mb-5">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-gray-950">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-3">Choose Your Plan</h2>
          <p className="text-center text-gray-400 mb-14">Get the level of access that fits your investment strategy</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 flex flex-col">
              <div className="text-gray-300 font-semibold text-lg mb-1">Free</div>
              <div className="text-5xl font-extrabold text-white mb-1">$0</div>
              <div className="text-gray-400 text-sm mb-6">Get started exploring</div>
              <ul className="space-y-2.5 flex-1 mb-8">
                {['Interactive county map', 'View auction dates', 'County-level data'].map((f) => (
                  <li key={f} className="flex items-center text-sm text-gray-300">
                    <i className="ri-check-line text-emerald-400 mr-2"></i>{f}
                  </li>
                ))}
                {['Property details', 'Search & filter', 'CSV export', 'Owner contact info', 'Email alerts'].map((f) => (
                  <li key={f} className="flex items-center text-sm text-gray-600">
                    <i className="ri-close-line text-gray-700 mr-2"></i>{f}
                  </li>
                ))}
              </ul>
              <a
                href="https://taxsale.realestatedevelopmentauthority.net"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="block text-center py-2.5 border border-gray-500 text-gray-300 rounded-md hover:border-emerald-500 hover:text-emerald-400 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Current Plan
              </a>
            </div>

            {/* Silver */}
            <div className="bg-gray-900 border-2 border-emerald-500 rounded-xl p-8 flex flex-col relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-gray-900 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                MOST POPULAR
              </div>
              <div className="text-emerald-400 font-semibold text-lg mb-1">Silver</div>
              <div className="text-5xl font-extrabold text-white mb-1">
                $29<span className="text-xl font-normal text-gray-400">/mo</span>
              </div>
              <div className="text-gray-400 text-sm mb-6">For serious investors</div>
              <ul className="space-y-2.5 flex-1 mb-8">
                {['Everything in Free', 'Full property details', 'Advanced search & filters', 'Export to CSV', 'Property history'].map((f) => (
                  <li key={f} className="flex items-center text-sm text-gray-300">
                    <i className="ri-check-line text-emerald-400 mr-2"></i>{f}
                  </li>
                ))}
                {['Owner contact info', 'Phone numbers', 'Email alerts'].map((f) => (
                  <li key={f} className="flex items-center text-sm text-gray-600">
                    <i className="ri-close-line text-gray-700 mr-2"></i>{f}
                  </li>
                ))}
              </ul>
              <a
                href="https://taxsale.realestatedevelopmentauthority.net"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="block text-center py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white rounded-md transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                Get Silver
              </a>
            </div>

            {/* Gold */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 flex flex-col">
              <div className="text-amber-400 font-semibold text-lg mb-1">Gold</div>
              <div className="text-5xl font-extrabold text-white mb-1">
                $99<span className="text-xl font-normal text-gray-400">/mo</span>
              </div>
              <div className="text-gray-400 text-sm mb-6">Maximum advantage</div>
              <ul className="space-y-2.5 flex-1 mb-8">
                {[
                  'Everything in Silver',
                  'Owner names & addresses',
                  'Phone numbers',
                  'Email addresses',
                  'New listing alerts',
                  'Priority support',
                  'Surplus funds data',
                  'Letter templates',
                ].map((f) => (
                  <li key={f} className="flex items-center text-sm text-gray-300">
                    <i className="ri-check-line text-amber-400 mr-2"></i>{f}
                  </li>
                ))}
              </ul>
              <a
                href="https://taxsale.realestatedevelopmentauthority.net"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="block text-center py-2.5 bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                Get Gold
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Frequently Asked Questions</h2>
          <p className="text-center text-gray-500 mb-12">Everything you need to know about Arizona tax foreclosures</p>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-emerald-700 py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find Your Next Deal?</h2>
          <p className="text-emerald-100 mb-8">
            Browse Arizona tax sale properties, research auction dates, and contact owners — all in one place.
          </p>
          <a
            href="https://taxsale.realestatedevelopmentauthority.net"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-block px-8 py-3 bg-white text-emerald-700 font-bold rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Visit AZ Tax Sales Site
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
